// Layout CSS
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

// import { TouchBackend } from "react-dnd-touch-backend"; // Will use later, see comment below.

import { serverGetUser, serverRedeemInviteKey } from "api/api-server-actions";
import { ForceReloadModal } from "components/ForceReloadModal";
import { LoadingScreen } from "components/LoadingScreen";
import { KeyboardShortcutsDialog } from "components/keyboard-shortcuts/KeyboardShortcutsDialog";
import { LoginView } from "components/sso/LoginView";
import { SignupView } from "components/sso/SignupView";
import { t } from "i18n/i18n";
import { HomeRoleProvider } from "providers/HomeRoleProvider";
import { IntercomProvider } from "providers/IntercomProvider";
import { KeyboardShortcutsController } from "providers/KeyboardShortcutsController";
import { MonitoringTasks } from "providers/MonitoringTasks";
import { Observers } from "providers/Observers";
import { PubsubWebsocketProvider } from "providers/PubsubWebsocketProvider";
import { SocketStatsCollectorProvider } from "providers/SocketStatsCollectorProvider";
import { store, useAppDispatch, useAppSelector } from "store";
import {
  selectCurrentCourseCode,
  selectCurrentOrgName,
  selectCurrentSectionTitle,
  selectCurrentTermTitle,
  selectCurrentUserComputedRole,
  selectCurrentUserId,
} from "store/selectors";
import { authStateActions } from "store/slices/auth";
import { toastErrorCatcher, toastLocalizedOperationCatcher } from "utils/alerts";
import { trackEvent, useTrackScreenViewEvent } from "utils/amplitude";
import { reportApiFailure } from "utils/api-errors";
import { clearOrgIdCookie, getOrgIdFromCookie } from "utils/auth";
import { useCheckMigrationStatus } from "utils/migration-status.tsx";
import { Outlet } from "utils/routing";
import { sectionHomeUrl } from "utils/urls";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

export default function Layout() {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectCurrentUserId);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isUserLoading, setIsUserLoading] = useState(true);

  useCheckMigrationStatus();

  // We don't use getQueryParam because this is only relevant on first load, and
  // we don't need to re-render the component when the query changes.
  const queryInviteLink = new URLSearchParams(window.location.search).get("invite_key");

  const shouldShowLogin = Boolean((!userId && !isUserLoading) || inviteError);
  const shouldShowSignup = Boolean(queryInviteLink || inviteError);

  useTrackScreenViewEvent(shouldShowSignup ? "Sign up page viewed" : "Sign in page viewed", {
    shouldTrack: shouldShowLogin,
  });

  // User login management
  const checkActiveUser = useCallback(
    (justLoggedIn?: boolean) => {
      serverGetUser()
        .then((userResponse) => {
          dispatch(authStateActions.setCurrentUser(userResponse));
          setIsUserLoading(false);

          // Event tracking
          const state = store.getState();
          const currentOrgName = selectCurrentOrgName(state) || "";
          const courseCode = selectCurrentCourseCode(state) || "";
          const currentSectionTitle = selectCurrentSectionTitle(state) || "";
          const currentTermTitle = selectCurrentTermTitle(state) || "";
          const computedRole = selectCurrentUserComputedRole(state);

          if (justLoggedIn) {
            trackEvent("Account - User signed in", {
              eventCategory: "General",
              userRole: computedRole,
              institutionName: currentOrgName,
              course: courseCode,
              sectionTitle: currentSectionTitle,
              courseTerm: currentTermTitle,
            });
          }
        })
        .catch((error: AxiosError | Error) => {
          dispatch(authStateActions.setCurrentUser(null));
          setIsUserLoading(false);
          if (!(error instanceof AxiosError)) {
            toastLocalizedOperationCatcher("get_user_data_failure")(error);
            reportApiFailure("Failed to get user", error);
            return;
          }

          const data = error.response?.data as Record<string, string>;
          if (data && typeof data === "object" && "code" in data) {
            if (data.code === "NOT_IN_THIS_ORG" && getOrgIdFromCookie()) {
              clearOrgIdCookie();
              window.location.reload();
            }
            toastErrorCatcher(error);
            reportApiFailure("User failed to authenticate", error);
          } else if (error.response?.status === 401 && !justLoggedIn) {
            // We're simply not authenticated yet, the OAuth token expired,
            // or we changed something about the OAuth code.
          } else {
            toastLocalizedOperationCatcher("failed_to_check_login_data")(error);
            reportApiFailure("Failed to get user", error);
          }
        });
    },
    [dispatch],
  );

  useEffect(() => {
    if (!userId) {
      checkActiveUser();
    }
  }, [checkActiveUser, userId]);

  // If a logged-in user arrives via an invite link, we redeem it in the background before
  // forwarding them to the relevant course view.
  useEffect(() => {
    if (userId && !isUserLoading && queryInviteLink && !inviteError) {
      setIsUserLoading(true);

      serverRedeemInviteKey(queryInviteLink)
        .then(({ section_id }) => {
          // Force a page refresh to make sure auth related calls are made to the right
          // org, in case the org has changed with the invite link.
          window.location.href = sectionHomeUrl(section_id);
        })
        .catch((error: AxiosError) => {
          const errorKey =
            error instanceof AxiosError && error.response?.status === 403
              ? "invalid_invite_key"
              : "unknown_error";

          setInviteError(t(`user_options_menu.redeem.${errorKey}`));
        })
        .finally(() => setIsUserLoading(false));
    }
  }, [queryInviteLink, isUserLoading, userId, inviteError]);

  if (shouldShowLogin) {
    return (
      <div className="flex h-full items-center justify-center">
        {shouldShowSignup ? (
          <SignupView
            checkActiveUser={checkActiveUser}
            setUserInviteError={setInviteError}
            userInviteError={inviteError}
          />
        ) : (
          <LoginView onLoggedIn={() => checkActiveUser(true)} />
        )}
      </div>
    );
  }

  if (!userId) {
    return <LoadingScreen />;
  }

  return (
    <div className="h-full w-full overflow-hidden">
      <ForceReloadModal />
      {/* SocketStatsCollectorProvider tracks stats from both pubsub and sharedb connections */}
      <SocketStatsCollectorProvider>
        {/* PubsubWebsocketProvider handles model updates, attendance, virtual class state */}
        <PubsubWebsocketProvider>
          <IntercomProvider>
            {/* key: reset hydrate if auth userId ever changes without unmounting the shell */}
            <HomeRoleProvider key={userId}>
              <Observers />
              <MonitoringTasks />
              {/* TODO: The DnDProvider will only work on mobile touch devices with the TouchBackend (which we will support).
                  However, the TouchBackend requires you to implement your own custom drag layer to display the UI
                  while dragging. The HTML5Backend automatically makes an image of your component and uses that, which
                  we are using now for expediency. Once the design is further along, we can switch over the backend as
                  this will also give us more control over the look and feel when the item is dragging (e.g. we can
                  remove the Edit/Delete icons then). At that point the react-dnd-html5-backend can be removed. */}
              {/* <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}> */}
              <DndProvider backend={HTML5Backend}>
                {/* TODO: This Error boundary should show a message indicating that the route itself has failed
                    and offer navigation to a different route. We could introduce a special kind of error that is
                    parsed differently, e.g. throw new QueryParamError that lets the user know the URL is malformed
                    somehow. */}
                <KeyboardShortcutsController />
                <KeyboardShortcutsDialog />
                <ErrorBoundary minimal>
                  <Outlet />
                </ErrorBoundary>
              </DndProvider>
            </HomeRoleProvider>
          </IntercomProvider>
        </PubsubWebsocketProvider>
      </SocketStatsCollectorProvider>
    </div>
  );
}
